import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  SlidersHorizontal, Eye, Calendar, TrendingUp, X, Filter
} from 'lucide-react';

export function PillContextMenu({
  pillCtxMenu, setPillCtxMenu, allFields, dateParts, setDateParts,
  slicerDisplayModes, setSlicerDisplayModes, secondYAxisConfig, setSecondYAxisConfig,
  activeVisual, visuals, openFieldProperties, handleBrowseField, removeFromAllShelves
}) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  // Reposition so the menu never spills off the bottom/right of the screen —
  // clamp it fully into the viewport (right-clicking a slicer near the bottom
  // used to cut off the lower options).
  useLayoutEffect(() => {
    if (!pillCtxMenu || !menuRef.current) { setPos(null); return; }
    const r = menuRef.current.getBoundingClientRect();
    let x = pillCtxMenu.x, y = pillCtxMenu.y;
    if (y + r.height > window.innerHeight - 8) y = Math.max(8, window.innerHeight - r.height - 8);
    if (x + r.width > window.innerWidth - 8) x = Math.max(8, window.innerWidth - r.width - 8);
    setPos({ x, y });
  }, [pillCtxMenu]);

  if (!pillCtxMenu) return null;

  return (
    <div className="fixed inset-0 z-[100]" onClick={() => setPillCtxMenu(null)} onContextMenu={(e) => { e.preventDefault(); setPillCtxMenu(null); }}>
      <div ref={menuRef} className="glass-card rounded-lg shadow-2xl border border-border/50 py-1 w-44 overflow-y-auto" style={{ position: 'fixed', left: (pos ? pos.x : pillCtxMenu.x), top: (pos ? pos.y : pillCtxMenu.y), maxHeight: 'calc(100vh - 16px)', zIndex: 101, visibility: pos ? 'visible' : 'hidden' }} onClick={(e) => e.stopPropagation()}>
        <button onClick={() => { openFieldProperties(pillCtxMenu.field); setPillCtxMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted/30 transition-colors" data-testid="ctx-field-props">
          <SlidersHorizontal className="w-3 h-3 text-muted-foreground" /> Properties
        </button>
        <button onClick={() => { handleBrowseField(allFields.find(f => f.name === pillCtxMenu.field) || { name: pillCtxMenu.field }); setPillCtxMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted/30 transition-colors" data-testid="ctx-field-browse">
          <Eye className="w-3 h-3 text-muted-foreground" /> Browse Data
        </button>
        {/* Date Part selector */}
        {(() => {
          let fieldMeta = allFields.find(f => f.name === pillCtxMenu.field);
          if (fieldMeta?._sourceField) fieldMeta = allFields.find(f => f.name === fieldMeta._sourceField) || fieldMeta;
          const isDate = fieldMeta && (fieldMeta.data_type === 'date' || fieldMeta.data_type === 'datetime' || fieldMeta.name.toLowerCase().includes('date'));
          const isDim = fieldMeta && fieldMeta.semantic_type === 'dimension';
          if (isDate && isDim) {
            const currentPart = dateParts[pillCtxMenu.field] || null;
            return (
              <React.Fragment>
                <div className="border-t border-border/30 my-1" />
                <p className="px-3 py-0.5 text-[8px] text-muted-foreground uppercase tracking-wider">Date Part</p>
                {[['none', 'Full Date'], ['year', 'Year'], ['quarter', 'Quarter'], ['month_year', 'Month / Year'], ['month', 'Month'], ['month_number', 'Month Number'], ['week', 'Week'], ['day', 'Day'], ['day_of_week', 'Day of Week'], ['hour', 'Hour']].map(([key, label]) => (
                  <button key={key} onClick={() => { setDateParts(prev => ({ ...prev, [pillCtxMenu.field]: key === 'none' ? null : key })); setPillCtxMenu(null); }} className={'w-full flex items-center gap-2 px-3 py-1 text-[11px] transition-colors ' + ((currentPart || 'none') === (key === 'none' ? 'none' : key) && (key === 'none' ? !currentPart : currentPart === key) ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-muted/30')}>
                    {(key === 'none' ? !currentPart : currentPart === key) ? <Calendar className="w-2.5 h-2.5" /> : <span className="w-2.5" />} {label}
                  </button>
                ))}
              </React.Fragment>
            );
          }
          return null;
        })()}
        {/* Second Y-Axis option */}
        {(() => {
          const cv2 = visuals.find(v => v.id === activeVisual);
          const vm2 = cv2?.valueMeasures || [];
          const vt2 = cv2?.vizType || '';
          const xyCharts = ['bar_chart', 'line_chart', 'area_chart'];
          const isValueShelf = pillCtxMenu.shelf?.includes('Value') || pillCtxMenu.shelf?.includes('Y-Axis');
          // Offer a secondary (right) axis for ANY value measure once there are
          // at least two — a ratio / percentage calculated measure is exactly
          // what belongs on the right axis, even when it's the first pill.
          const canRightAxis = vm2.includes(pillCtxMenu.field) && vm2.length >= 2;
          const curS2 = secondYAxisConfig[activeVisual];
          if (xyCharts.includes(vt2) && isValueShelf && canRightAxis) {
            return (
              <React.Fragment>
                <div className="border-t border-border/30 my-1" />
                <p className="px-3 py-0.5 text-[8px] text-muted-foreground uppercase tracking-wider">Second Y-Axis</p>
                {curS2?.field === pillCtxMenu.field ? (
                  <button onClick={() => { setSecondYAxisConfig(prev => { const c = {...prev}; delete c[activeVisual]; return c; }); setPillCtxMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1 text-[11px] text-destructive hover:bg-destructive/10 transition-colors">
                    <X className="w-2.5 h-2.5" /> Remove from Right Axis
                  </button>
                ) : (
                  [['bar', 'Bar Chart'], ['line', 'Line Chart'], ['area', 'Area Chart']].map(([key, label]) => (
                    <button key={key} data-testid={`ctx-right-axis-${key}`} onClick={() => { setSecondYAxisConfig(prev => ({...prev, [activeVisual]: { field: pillCtxMenu.field, chartType: key }})); setPillCtxMenu(null); }} className={'w-full flex items-center gap-2 px-3 py-1 text-[11px] transition-colors ' + (curS2?.field === pillCtxMenu.field && curS2?.chartType === key ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-muted/30')}>
                      <TrendingUp className="w-2.5 h-2.5 text-muted-foreground" /> {label} (Right Axis)
                    </button>
                  ))
                )}
              </React.Fragment>
            );
          }
          return null;
        })()}
        {/* Slicer display mode */}
        {pillCtxMenu.shelf === 'Slicers' && (
          <React.Fragment>
            <div className="border-t border-border/30 my-1" />
            <p className="px-3 py-0.5 text-[8px] text-muted-foreground uppercase tracking-wider">Show slicer as…</p>
            {[['dropdown','Dropdown'],['multi','Multi-Select'],['buttons','Buttons'],['radio','Radio List'],['search','Searchable']].map(([key, label]) => (
              <button key={key} onClick={() => { setSlicerDisplayModes(prev => ({ ...prev, [pillCtxMenu.field]: key })); setPillCtxMenu(null); }} className={'w-full flex items-center gap-2 px-3 py-1 text-[11px] transition-colors ' + ((slicerDisplayModes[pillCtxMenu.field] || 'dropdown') === key ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-muted/30')}>
                {(slicerDisplayModes[pillCtxMenu.field] || 'dropdown') === key ? <Filter className="w-2.5 h-2.5" /> : <span className="w-2.5" />} {label}
              </button>
            ))}
            {(() => { const fm = allFields.find(f => f.name === pillCtxMenu.field); return fm && (fm.data_type === 'date' || fm.data_type === 'datetime' || fm.name.toLowerCase().includes('date')) ? [['date_picker','Date Picker'],['date_range','Date Range']].map(([key, label]) => (
              <button key={key} onClick={() => { setSlicerDisplayModes(prev => ({ ...prev, [pillCtxMenu.field]: key })); setPillCtxMenu(null); }} className={'w-full flex items-center gap-2 px-3 py-1 text-[11px] transition-colors ' + ((slicerDisplayModes[pillCtxMenu.field] || 'dropdown') === key ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-muted/30')}>
                {(slicerDisplayModes[pillCtxMenu.field] || 'dropdown') === key ? <Filter className="w-2.5 h-2.5" /> : <span className="w-2.5" />} {label}
              </button>
            )) : null; })()}
          </React.Fragment>
        )}
        <div className="border-t border-border/30 my-1" />
        <button onClick={() => { removeFromAllShelves(pillCtxMenu.field); setPillCtxMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10 transition-colors" data-testid="ctx-field-remove">
          <X className="w-3 h-3" /> Remove
        </button>
      </div>
    </div>
  );
}
